import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
const logoImgPath = '/ILPQCLogo_without_bg.png'

let _timerWorker: Worker | null = null
const _timerCallbacks = new Map<number, () => void>()
let _timerId = 0

const initTimerWorker = () => {
    if (_timerWorker) return
    try {
        const blob = new Blob([
            'self.onmessage=function(e){setTimeout(function(){self.postMessage(e.data.id)},e.data.ms)}'
        ], { type: 'application/javascript' })
        _timerWorker = new Worker(URL.createObjectURL(blob))
        _timerWorker.onmessage = (e: MessageEvent) => {
            const cb = _timerCallbacks.get(e.data)
            if (cb) {
                _timerCallbacks.delete(e.data)
                cb()
            }
        }
    } catch {
        _timerWorker = null
    }
}

const reliableDelay = (ms: number): Promise<void> => {
    return new Promise(resolve => {
        if (_timerWorker) {
            const id = _timerId++
            _timerCallbacks.set(id, resolve)
            _timerWorker.postMessage({ id, ms })
        } else {
            setTimeout(resolve, ms)
        }
    })
}

const reliableSetTimeout = (fn: () => void, ms: number) => {
    if (_timerWorker) {
        const id = _timerId++
        _timerCallbacks.set(id, fn)
        _timerWorker.postMessage({ id, ms })
    } else {
        setTimeout(fn, ms)
    }
}

const cleanupTimerWorker = () => {
    if (_timerWorker) {
        _timerWorker.terminate()
        _timerWorker = null
    }
    _timerCallbacks.clear()
    _timerId = 0
}

let _keepAliveCtx: AudioContext | null = null
let _keepAliveOsc: OscillatorNode | null = null

const startKeepAlive = () => {
    try {
        const ctx = new AudioContext()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        gain.gain.value = 0
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        _keepAliveCtx = ctx
        _keepAliveOsc = osc
    } catch {
        _keepAliveCtx = null
        _keepAliveOsc = null
    }
}

const stopKeepAlive = () => {
    try {
        if (_keepAliveOsc) { _keepAliveOsc.stop(); _keepAliveOsc = null }
        if (_keepAliveCtx) { _keepAliveCtx.close(); _keepAliveCtx = null }
    } catch {
        _keepAliveCtx = null
        _keepAliveOsc = null
    }
}

interface ExportPage {
    url: string
    title: string
}

const isElementVisible = (element: Element, win: Window): boolean => {
    if (!element || element.nodeType !== 1) return false
    const style = win.getComputedStyle(element)
    if (!style) return false
    return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        parseFloat(style.opacity || '1') !== 0 &&
        (element as HTMLElement).offsetWidth > 0 &&
        (element as HTMLElement).offsetHeight > 0
    )
}

const hasVisibleLoadingIndicators = (doc: Document, win: Window): boolean => {
    if (!doc || !win) return false

    const selectors = [
        '#loading-notice',
        "[id*='loading']",
        '.loading',
        '.loading-overlay',
        '.loading-container',
        '.spinner',
        '.lds-ring',
        '.lds-spinner',
        '.apexcharts-loading',
        '[aria-busy="true"]'
    ]

    for (const sel of selectors) {
        const nodes = Array.from(doc.querySelectorAll(sel))
        if (nodes.some(n => isElementVisible(n, win))) return true
    }

    const all = Array.from(doc.querySelectorAll('*')).slice(0, 500)
    for (const el of all) {
        if (!el || !el.textContent) continue
        const text = el.textContent.toLowerCase()
        if (/loading/i.test(text) && isElementVisible(el, win)) {
            const hasSpinner = el.querySelector('.spinner') || el.classList.contains('loading-container')
            if (hasSpinner || text.trim() === 'loading...' || text.trim() === 'loading') {
                return true
            }
        }
    }

    return false
}

export const exportDashboardAsPDF = async (
    pages: ExportPage[],
    dashboardTitle: string,
    onProgress: (progress: number, status: string) => void
) => {
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.top = '0'
    iframe.style.left = '0'
    iframe.style.width = '1280px'
    iframe.style.height = '2200px'
    iframe.style.opacity = '0'
    iframe.style.zIndex = '-9999'
    iframe.style.pointerEvents = 'none'
    document.body.appendChild(iframe)

    try {
        initTimerWorker()
        startKeepAlive()
        const pageImages: { title: string; imageData: string; width: number; height: number }[] = []

        let analyzeProgress = 0
        let captureProgress = 0
        let composeProgress = 0
        const weights = { analyze: 20, capture: 60, compose: 20 }

        const updateProgress = () => {
            const total = analyzeProgress * weights.analyze +
                captureProgress * weights.capture +
                composeProgress * weights.compose
            return Math.round(total)
        }

        const logoData = await loadImage(logoImgPath)

        for (let i = 0; i < pages.length; i++) {
            const page = pages[i]
            const isStructure = page.title === 'Structure Measures'
            const isPrem = page.title.includes('PREM') || page.title.includes('Completion') || page.title.includes('Patient Agreement')
            const isOutcome = page.title === 'Outcome Measures'
            const isStatusAtGlance = page.title === 'Status at a Glance'
            const isEngagement = page.title === 'Engagement Measures'
            const isEducation = page.title === 'Education Measures'
            const isLineChartPage = page.title === 'Clinical Team Education' ||
                page.title === 'Patient Education' ||
                page.title === 'PMH Screening' ||
                page.title === 'Screening' ||
                page.title === 'Outcome Measures' ||
                page.title === 'Education Measures' ||
                page.title === 'Patient Agreement'

            if (isOutcome) {
                iframe.style.height = '4500px'
            } else if (isLineChartPage) {
                iframe.style.height = '3000px'
            } else if (isStatusAtGlance) {
                iframe.style.height = '2800px'
            } else if (isEngagement || isEducation) {
                iframe.style.height = '2500px'
            } else if (isStructure) {
                iframe.style.height = '1800px'
            } else {
                iframe.style.height = '2200px'
            }

            iframe.style.width = '1280px'

            analyzeProgress = i / pages.length
            onProgress(updateProgress(), `Loading ${page.title}...`)

            const currentParams = new URLSearchParams(window.location.search)
            const pageUrl = new URL(page.url, window.location.origin)

            currentParams.forEach((value, key) => {
                pageUrl.searchParams.set(key, value)
            })

            pageUrl.searchParams.set('ts', Date.now().toString())

            const cacheBustedUrl = pageUrl.toString()
            await loadPageInIframe(iframe, cacheBustedUrl)

            try {
                const targetWindow = iframe.contentWindow
                if (targetWindow) {
                    for (let j = 0; j < sessionStorage.length; j++) {
                        const key = sessionStorage.key(j)
                        if (key) {
                            const value = sessionStorage.getItem(key)
                            if (value) {
                                targetWindow.sessionStorage.setItem(key, value)
                            }
                        }
                    }

                    const selectedHospitalIds = sessionStorage.getItem('selectedHospitalIds')
                    const isMultiSelect = sessionStorage.getItem('isMultiSelect')

                    try {
                        targetWindow.postMessage({
                            hospitalIds: selectedHospitalIds ? JSON.parse(selectedHospitalIds) : [],
                            isMultiSelect: isMultiSelect === 'true'
                        }, '*')
                    } catch (postError) {
                        console.warn('Could not send postMessage to iframe:', postError)
                    }

                    const storageEvent = new StorageEvent('storage', {
                        key: 'selectedHospitalIds',
                        newValue: sessionStorage.getItem('selectedHospitalIds'),
                        url: targetWindow.location.href,
                        storageArea: targetWindow.sessionStorage
                    })
                    targetWindow.dispatchEvent(storageEvent)

                    const maxWaitTime = isOutcome ? 8000 : (isLineChartPage ? 6000 : (isStatusAtGlance ? 4000 : (isEngagement || isEducation ? 3000 : 2000)))
                    const checkInterval = 250
                    let waited = 0

                    while (waited < maxWaitTime) {
                        await reliableDelay(checkInterval)
                        waited += checkInterval

                        const targetDoc = iframe.contentDocument
                        if (targetDoc) {
                            const bodyText = targetDoc.body?.textContent?.toLowerCase() || ''
                            const hasNoDataMessage = bodyText.includes('no data available') ||
                                bodyText.includes('aggregate site data not available') ||
                                bodyText.includes('no aggregation')

                            if (hasNoDataMessage && !hasVisibleLoadingIndicators(targetDoc, targetWindow)) {
                                break
                            }

                            const apexCanvases = targetDoc.querySelectorAll('.apexcharts-canvas')
                            const totalCharts = apexCanvases.length
                            let readyCharts = 0
                            apexCanvases.forEach(canvas => {
                                const svg = canvas.querySelector('svg')
                                if (!svg) return
                                const series = svg.querySelectorAll('g.apexcharts-series path, g.apexcharts-series line')
                                const axes = svg.querySelectorAll('.apexcharts-xaxis, .apexcharts-yaxis')
                                if (series.length > 0 || axes.length >= 2) readyCharts++
                            })

                            if (totalCharts > 0 && readyCharts >= totalCharts && !hasVisibleLoadingIndicators(targetDoc, targetWindow)) {
                                break
                            }
                        }
                    }
                }
            } catch (error) {
                console.warn('Could not copy sessionStorage to iframe:', error)
            }

            await waitForContent(iframe, page.title, (prog) => {
                analyzeProgress = (i + prog) / pages.length
                onProgress(updateProgress(), `Analyzing ${page.title}...`)
            })

            const postDoc = iframe.contentDocument
            const postWin = iframe.contentWindow
            if (postDoc && postWin && hasVisibleLoadingIndicators(postDoc, postWin)) {
                let extraWait = 0
                while (extraWait < 5000) {
                    await reliableDelay(500)
                    extraWait += 500
                    if (!hasVisibleLoadingIndicators(postDoc, postWin)) break
                }
            }

            const isPmhProcessPage =
                page.title === 'Clinical Team Education' ||
                page.title === 'Patient Education' ||
                page.title === 'PMH Screening'
            if (isPmhProcessPage) {
                const iframeWin = iframe.contentWindow as any
                const instances: any[] = iframeWin?.Apex?._chartInstances || []
                for (const entry of instances) {
                    const chart = entry?.chart
                    if (!chart) continue
                    try {
                        const containerW = chart.el?.parentElement?.clientWidth || chart.el?.clientWidth
                        await chart.updateOptions({
                            chart: { width: containerW || '100%' },
                            xaxis: { labels: { rotate: -45, rotateAlways: true } }
                        }, true, false)
                    } catch (_e) { /* ignore */ }
                }
                if (instances.length > 0) {
                    await reliableDelay(500)
                }
            }

            captureProgress = i / pages.length
            onProgress(updateProgress(), `Capturing ${page.title}...`)

            if (isOutcome) {
                const iframeDoc = iframe.contentDocument
                const iframeWin = iframe.contentWindow

                if (iframeDoc && iframeWin) {
                    const target = iframeDoc.getElementById('dashboard') ||
                        iframeDoc.querySelector('.content-wrapper') as HTMLElement ||
                        iframeDoc.body
                    const targetEl = target as HTMLElement

                    const sidebar = iframeDoc.querySelector('.sidebar, nav.sidebar, .navigation') as HTMLElement
                    const savedSidebarDisplay = sidebar?.style.display
                    if (sidebar) sidebar.style.display = 'none'

                    const contentArea = iframeDoc.querySelector('.content-area, .main-content') as HTMLElement
                    const savedContentMargin = contentArea?.style.marginLeft
                    if (contentArea) contentArea.style.marginLeft = '0'

                    const savedPadding = {
                        left: targetEl.style.paddingLeft,
                        right: targetEl.style.paddingRight,
                        top: targetEl.style.paddingTop,
                        bottom: targetEl.style.paddingBottom,
                        boxSizing: targetEl.style.boxSizing
                    }
                    targetEl.style.paddingLeft = '4px'
                    targetEl.style.paddingRight = '4px'
                    targetEl.style.paddingTop = '12px'
                    targetEl.style.paddingBottom = '12px'
                    targetEl.style.boxSizing = 'border-box'

                    boostFontSizes(iframeDoc, iframeWin, false)

                    const pmhTitleStyle = iframeDoc.createElement('style')
                    pmhTitleStyle.textContent = `
                        .pmh-outcome-measures-page .page-title { font-size: 38px !important; }
                        .pmh-outcome-measures-page .section-title { font-size: 28px !important; }
                    `
                    iframeDoc.head.appendChild(pmhTitleStyle)
                    await reliableDelay(100)

                    const apexInstances: any[] = (iframeWin as any)?.Apex?._chartInstances || []
                    for (const entry of apexInstances) {
                        const chart = entry?.chart
                        if (!chart) continue
                        try {
                            const containerW = chart.el?.parentElement?.clientWidth || chart.el?.clientWidth
                            await chart.updateOptions({
                                chart: { width: containerW || '100%' },
                                xaxis: { labels: { rotate: -45, rotateAlways: true } }
                            }, true, false)
                        } catch (_e) { /* ignore */ }
                    }
                    await reliableDelay(300)

                    const controlContainers = Array.from(iframeDoc.querySelectorAll('.strat-controls-container, .interval-controls-container'))
                    const savedControlDisplays = controlContainers.map(el => (el as HTMLElement).style.display)
                    controlContainers.forEach(el => { (el as HTMLElement).style.display = 'none' })

                    const chartSections = Array.from(iframeDoc.querySelectorAll('.chart-section'))
                    const pageHeader = iframeDoc.querySelector('.page-header') as HTMLElement

                    for (let g = 0; g < chartSections.length; g += 2) {
                        const isFirstGroup = g === 0
                        const chartsInGroup = Math.min(g + 1, chartSections.length - 1) - g + 1
                        const isSingleChartGroup = chartsInGroup === 1

                        chartSections.forEach((section, idx) => {
                            const inGroup = idx >= g && idx <= Math.min(g + 1, chartSections.length - 1)
                            ;(section as HTMLElement).style.display = inGroup ? '' : 'none'
                        })

                        if (!isFirstGroup && pageHeader) pageHeader.style.display = 'none'

                        if (isSingleChartGroup) {
                            targetEl.style.paddingLeft = '48px'
                            targetEl.style.paddingRight = '48px'
                        } else {
                            targetEl.style.paddingLeft = '4px'
                            targetEl.style.paddingRight = '4px'
                        }

                        iframeWin.dispatchEvent(new Event('resize'))
                        await reliableDelay(500)

                        iframe.style.height = `${target.scrollHeight + 50}px`
                        await reliableDelay(200)

                        const canvas = await html2canvas(target, {
                            scale: 2,
                            useCORS: true,
                            allowTaint: true,
                            logging: false,
                            backgroundColor: '#f0f6fa',
                            imageTimeout: 15000,
                            removeContainer: true,
                            ignoreElements: (el: any) => {
                                return el.classList && (
                                    el.classList.contains('hamburger') ||
                                    el.classList.contains('export-menu') ||
                                    el.classList.contains('export-dropdown') ||
                                    el.classList.contains('menu-container') ||
                                    el.classList.contains('export-menu-container') ||
                                    el.classList.contains('sidebar') ||
                                    el.classList.contains('navigation') ||
                                    el.classList.contains('hospital-selector') ||
                                    el.classList.contains('hospital-dropdown') ||
                                    el.classList.contains('dropdown-header') ||
                                    el.classList.contains('dropdown-list') ||
                                    el.classList.contains('custom-select') ||
                                    el.classList.contains('header-controls') ||
                                    el.classList.contains('strat-controls-container') ||
                                    el.classList.contains('interval-controls-container') ||
                                    el.classList.contains('controls') ||
                                    el.classList.contains('tmb-question-pill') ||
                                    el.classList.contains('tmb-question-dropdown') ||
                                    el.tagName === 'NAV' ||
                                    el.tagName === 'HEADER' ||
                                    el.tagName === 'SELECT'
                                )
                            }
                        } as any)

                        pageImages.push({
                            title: page.title,
                            imageData: canvas.toDataURL('image/jpeg', 0.85),
                            width: canvas.width,
                            height: canvas.height
                        })

                        if (!isFirstGroup && pageHeader) pageHeader.style.display = ''
                    }

                    chartSections.forEach(section => {
                        ;(section as HTMLElement).style.display = ''
                    })
                    controlContainers.forEach((el, idx) => {
                        ;(el as HTMLElement).style.display = savedControlDisplays[idx] || ''
                    })
                    if (sidebar) sidebar.style.display = savedSidebarDisplay || ''
                    if (contentArea) contentArea.style.marginLeft = savedContentMargin || ''
                    targetEl.style.paddingLeft = savedPadding.left
                    targetEl.style.paddingRight = savedPadding.right
                    targetEl.style.paddingTop = savedPadding.top
                    targetEl.style.paddingBottom = savedPadding.bottom
                    targetEl.style.boxSizing = savedPadding.boxSizing
                }

                captureProgress = (i + 1) / pages.length
                onProgress(updateProgress(), `Captured ${page.title}`)
                continue
            }

            const maxRetries = isStructure ? 2 : (isPrem ? 2 : 1)
            let captureResult: CaptureResult | null = null

            for (let retry = 0; retry < maxRetries; retry++) {
                captureResult = await captureContent(iframe, page.title)

                const doc = iframe.contentDocument
                const win = iframe.contentWindow
                if (!doc || !win) break

                const canvas = captureResult?.canvas
                const isBlankCanvas = canvas && canvas.width > 0 && canvas.height > 0 ? false : true
                const hasLoading = hasVisibleLoadingIndicators(doc, win)

                if (!isBlankCanvas && !hasLoading) break

                if (retry < maxRetries - 1) {
                    await reliableDelay(300)
                }
            }

            if (captureResult) {
                const canvas = captureResult.canvas
                const splitPoints = captureResult.splitPoints

                let targetPdfPages = 1
                if (splitPoints.length > 0) {
                    if (isStatusAtGlance) {
                        targetPdfPages = 2
                    } else if (splitPoints.length >= 2) {
                        targetPdfPages = Math.ceil((splitPoints.length + 1) / 2)
                    }
                }

                if (targetPdfPages <= 1 || splitPoints.length === 0) {
                    pageImages.push({
                        title: page.title,
                        imageData: canvas.toDataURL('image/jpeg', 0.85),
                        width: canvas.width,
                        height: canvas.height
                    })
                } else {
                    const pageBreakPoints: number[] = []

                    if (isStatusAtGlance) {
                        const midIdx = Math.floor(splitPoints.length / 2)
                        pageBreakPoints.push(splitPoints[midIdx])
                    } else {
                        for (let sp = 1; sp < splitPoints.length; sp += 2) {
                            pageBreakPoints.push(splitPoints[sp])
                        }
                    }

                    const sliceBoundaries = [0, ...pageBreakPoints, canvas.height]
                    const trimTopPx = isStatusAtGlance ? 0 : -350
                    const trimBottomPx = isStatusAtGlance ? 120 : 400
                    const padPx = 80

                    for (let s = 0; s < sliceBoundaries.length - 1; s++) {
                        const isFirst = s === 0
                        const isLast = s === sliceBoundaries.length - 2
                        const sliceTop = isFirst ? sliceBoundaries[s] : sliceBoundaries[s] + trimTopPx
                        const sliceBottom = isLast ? sliceBoundaries[s + 1] : sliceBoundaries[s + 1] - trimBottomPx
                        const sliceH = sliceBottom - sliceTop
                        if (sliceH <= 0) continue

                        if (isFirst) {
                            const sliceCanvas = document.createElement('canvas')
                            sliceCanvas.width = canvas.width
                            sliceCanvas.height = sliceH
                            const ctx = sliceCanvas.getContext('2d')
                            if (ctx) {
                                ctx.drawImage(canvas, 0, sliceTop, canvas.width, sliceH, 0, 0, canvas.width, sliceH)
                                pageImages.push({
                                    title: page.title,
                                    imageData: sliceCanvas.toDataURL('image/jpeg', 0.85),
                                    width: sliceCanvas.width,
                                    height: sliceCanvas.height
                                })
                            }
                        } else {
                            const paddedW = canvas.width + padPx * 2
                            const paddedH = sliceH + padPx
                            const sliceCanvas = document.createElement('canvas')
                            sliceCanvas.width = paddedW
                            sliceCanvas.height = paddedH
                            const ctx = sliceCanvas.getContext('2d')
                            if (ctx) {
                                ctx.fillStyle = '#f0f6fa'
                                ctx.fillRect(0, 0, paddedW, paddedH)
                                ctx.drawImage(canvas, 0, sliceTop, canvas.width, sliceH, padPx, padPx, canvas.width, sliceH)
                                pageImages.push({
                                    title: page.title,
                                    imageData: sliceCanvas.toDataURL('image/jpeg', 0.85),
                                    width: sliceCanvas.width,
                                    height: sliceCanvas.height
                                })
                            }
                        }
                    }
                }
            }

            captureProgress = (i + 1) / pages.length
            onProgress(updateProgress(), `Captured ${page.title}`)
        }

        composeProgress = 0
        onProgress(updateProgress(), 'Generating PDF...')

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        })

        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        const margin = 6
        const headerHeight = 16
        const footerHeight = 10
        const contentMarginX = 6

        const siteBgCss = window.getComputedStyle(document.body).backgroundColor || '#ffffff'
        const cssToRgbArray = (c: string): [number, number, number] => {
            if (!c) return [255, 255, 255]
            if (c.startsWith('#')) {
                const hex = c.replace('#', '')
                const full = hex.length === 3 ? hex.split('').map(h => h + h).join('') : hex
                const n = parseInt(full, 16)
                return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
            }
            const m = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
            return m ? [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)] : [255, 255, 255]
        }
        const pdfBg = cssToRgbArray(siteBgCss)

        pageImages.forEach((img, index) => {
            if (index > 0) doc.addPage()

            doc.setFillColor(pdfBg[0], pdfBg[1], pdfBg[2])
            doc.rect(0, 0, pageWidth, pageHeight, 'F')

            if (logoData) {
                const logoW = 22
                let logoH = (logoData.height / logoData.width) * logoW
                const maxLogoH = 10
                if (logoH > maxLogoH) {
                    logoH = maxLogoH
                }
                doc.addImage(logoData.dataUrl, 'PNG', margin, 2, logoW, logoH)
            }

            doc.setFontSize(11)
            doc.setTextColor(28, 73, 139)
            doc.setFont('helvetica', 'bold')
            doc.text(img.title, pageWidth / 2, 7, { align: 'center' })

            const savedIds = sessionStorage.getItem('selectedHospitalIds')
            const hospitalNames = sessionStorage.getItem('hospitalNames')
            const hospitalIds = savedIds ? JSON.parse(savedIds) : []
            const namesMap: Record<string, string> = hospitalNames ? JSON.parse(hospitalNames) : {}

            let hospitalInfo = ''
            if (hospitalIds.length === 1) {
                const id = hospitalIds[0]
                const name = namesMap[id] || ''
                if (name) {
                    const nameHasId = name.startsWith(id + ' -') || name.startsWith(id + '-')
                    const displayName = nameHasId ? name : `${id} - ${name}`
                    hospitalInfo = `Hospital: ${displayName}`
                } else {
                    hospitalInfo = `Hospital ID: ${id}`
                }
            } else if (hospitalIds.length >= 2 && hospitalIds.length <= 6) {
                hospitalInfo = hospitalIds.join(', ')
            } else if (hospitalIds.length > 6) {
                hospitalInfo = `${hospitalIds.length} Hospitals Selected`
            }

            if (hospitalInfo) {
                doc.setFontSize(8)
                doc.setTextColor(68, 68, 68)
                doc.setFont('helvetica', 'normal')
                doc.text(hospitalInfo, pageWidth / 2, 12, { align: 'center' })
            }

            doc.setDrawColor(220, 220, 220)
            doc.setLineWidth(0.2)
            doc.line(margin, 14, pageWidth - margin, 14)

            const contentBottomGap = 4
            const availableH = pageHeight - headerHeight - footerHeight - contentBottomGap
            const availableW = pageWidth - 2 * contentMarginX

            const ratio = img.width / img.height || 1.6
            let w = availableW
            let h = w / ratio

            if (h > availableH) {
                h = availableH
                w = h * ratio
            }

            const x = (pageWidth - w) / 2
            const y = headerHeight

            doc.addImage(img.imageData, 'JPEG', x, y, w, h, undefined, 'FAST')

            const footerY = pageHeight - footerHeight
            doc.setDrawColor(220, 220, 220)
            doc.setLineWidth(0.2)
            doc.line(margin, footerY, pageWidth - margin, footerY)

            doc.setFontSize(6)
            doc.setTextColor(100, 100, 100)
            doc.setFont('helvetica', 'normal')
            doc.text(`Page ${index + 1} of ${pageImages.length}`, pageWidth / 2, footerY + 4, { align: 'center' })

            const now = new Date()
            const dateOnly = now.toISOString().slice(0, 10)
            doc.text(dateOnly, pageWidth - margin, footerY + 4, { align: 'right' })

            doc.setFontSize(6)
            doc.setTextColor(28, 73, 139)
            doc.text('Illinois Perinatal Quality Collaborative', margin, footerY + 4)

            composeProgress = (index + 1) / pageImages.length
            onProgress(updateProgress(), 'Composing PDF...')
        })

        composeProgress = 1
        onProgress(100, 'Done!')

        const timestamp = new Date().toISOString().split('T')[0]
        const pdfName = `${dashboardTitle.replace(/\s+/g, '_')}_Report_${timestamp}.pdf`

        await reliableDelay(300)
        doc.save(pdfName)

    } catch (error) {
        console.error('Export failed:', error)
        throw error
    } finally {
        document.body.removeChild(iframe)
        stopKeepAlive()
        cleanupTimerWorker()
    }
}

const loadPageInIframe = (iframe: HTMLIFrameElement, url: string): Promise<void> => {
    return new Promise((resolve) => {
        let resolved = false
        const handleLoad = () => {
            if (resolved) return
            resolved = true
            iframe.removeEventListener('load', handleLoad)
            resolve()
        }
        iframe.addEventListener('load', handleLoad)
        iframe.src = url

        reliableSetTimeout(() => {
            if (!resolved) {
                resolved = true
                iframe.removeEventListener('load', handleLoad)
                console.warn('Iframe load timeout - proceeding with available content')
                resolve()
            }
        }, 15000)
    })
}

const waitForContent = (
    iframe: HTMLIFrameElement,
    pageTitle: string,
    onProgress: (progress: number) => void
): Promise<void> => {
    return new Promise((resolve) => {
        const doc = iframe.contentDocument
        const win = iframe.contentWindow
        if (!doc || !win) {
            resolve()
            return
        }

        const isStructure = pageTitle === 'Structure Measures'
        const isPrem = pageTitle.includes('PREM') || pageTitle.includes('Completion')
        const isStatusAtGlance = pageTitle === 'Status at a Glance'
        const isEngagement = pageTitle === 'Engagement Measures'
        const isEducation = pageTitle === 'Education Measures'

        let checkInterval = 250
        let maxWait = isStatusAtGlance ? 8000 : ((isEngagement || isEducation) ? 7000 : 6000)
        let elapsed = 0

        const check = () => {
            if (!doc.body) {
                resolve()
                return
            }

            const getDisplay = (el: Element | null) => {
                if (!el) return 'none'
                try {
                    return win.getComputedStyle(el).display
                } catch {
                    return 'none'
                }
            }

            const multiselectNotice = doc.querySelector('#multiselect-notice, [class*="multiselect"]')
            const nodataNotice = doc.querySelector('#nodata-notice, [class*="nodata"]')
            const hasBlockingNotice =
                (multiselectNotice && getDisplay(multiselectNotice) !== 'none') ||
                (nodataNotice && getDisplay(nodataNotice) !== 'none')

            if (hasBlockingNotice) {
                resolve()
                return
            }

            if (hasVisibleLoadingIndicators(doc, win)) {
                if (elapsed >= maxWait) {
                    resolve()
                    return
                }
                elapsed += checkInterval
                onProgress(Math.min(0.9, elapsed / maxWait))
                reliableSetTimeout(check, checkInterval)
                return
            }

            const bodyText = doc.body.textContent?.toLowerCase() || ''
            const hasNoDataMessage = bodyText.includes('no data available') ||
                bodyText.includes('aggregate site data not available') ||
                bodyText.includes('no aggregation')

            if (hasNoDataMessage) {
                onProgress(1)
                resolve()
                return
            }

            const apexCharts = doc.querySelectorAll('.apexcharts-canvas')
            let apexChartsReady = true
            let apexReadyCount = 0

            apexCharts.forEach(chartCanvas => {
                const svg = chartCanvas.querySelector('svg')
                if (!svg) {
                    apexChartsReady = false
                } else {
                    const seriesElements = svg.querySelectorAll('g.apexcharts-series path, g.apexcharts-series line, g.apexcharts-series circle, g.apexcharts-series rect')
                    const hasDataSeries = seriesElements.length > 0
                    const elemCount = svg.querySelectorAll('path, rect, circle, line, polygon, polyline').length

                    if (!hasDataSeries && elemCount < 10) {
                        apexChartsReady = false
                    } else if (hasDataSeries) {
                        let hasRealData = false
                        seriesElements.forEach(el => {
                            const d = el.getAttribute('d')
                            const points = el.getAttribute('points')
                            if ((d && d.length > 10) || (points && points.length > 10)) {
                                hasRealData = true
                            }
                        })
                        if (hasRealData) {
                            apexReadyCount++
                        } else {
                            apexChartsReady = false
                        }
                    } else if (elemCount >= 10) {
                        apexReadyCount++
                    }
                }
            })

            if (isPrem) {
                const svgs = doc.querySelectorAll('.apexcharts-canvas svg')
                const haveSeries = Array.from(svgs).some(svg => {
                    const series = svg.querySelector('g.apexcharts-series path, g.apexcharts-series line')
                    if (series) {
                        const d = series.getAttribute('d')
                        return d && d.length > 10
                    }
                    return false
                })
                if (!haveSeries && elapsed < 5000) {
                    apexChartsReady = false
                }
            }

            const canvasCharts = doc.querySelectorAll("canvas:not([id*='html2canvas'])")
            let allCanvasReady = true
            let canvasReadyCount = 0

            canvasCharts.forEach(canvas => {
                const c = canvas as HTMLCanvasElement
                if (c.width === 0 || c.height === 0) {
                    allCanvasReady = false
                } else {
                    canvasReadyCount++
                    try {
                        const ctx = c.getContext('2d', { willReadFrequently: true })
                        if (ctx) {
                            const sample = (x: number, y: number, w: number, h: number) => {
                                const rw = Math.max(8, Math.min(w, 32))
                                const rh = Math.max(8, Math.min(h, 32))
                                const img = ctx.getImageData(x, y, rw, rh).data
                                for (let i = 0; i < img.length; i += 4) {
                                    if (img[i] !== 255 || img[i + 1] !== 255 || img[i + 2] !== 255) return true
                                }
                                return false
                            }
                            const cx = Math.floor(c.width / 2)
                            const cy = Math.floor(c.height / 2)
                            if (!sample(0, 0, 24, 24) && !sample(cx - 12, cy - 12, 24, 24)) {
                                allCanvasReady = false
                            }
                        }
                    } catch {
                    }
                }
            })

            let structureReady = true
            if (isStructure) {
                const table = doc.querySelector('#structureTable, table')
                const tableRows = table ? table.querySelectorAll('tbody tr').length : 0
                const statusDots = doc.querySelectorAll('.status-dot, .legend-dot')
                const hasColoredDots = Array.from(statusDots).some(dot => {
                    const color = win.getComputedStyle(dot as Element).backgroundColor
                    return color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent'
                })

                structureReady = tableRows > 0 && hasColoredDots

                if (!structureReady) {
                    checkInterval = 300
                    maxWait = 12000
                }
            }

            const hasCharts = apexCharts.length > 0 || canvasCharts.length > 0
            const chartsReady = (hasCharts && apexChartsReady && allCanvasReady) || !hasCharts

            const totalCharts = apexCharts.length + canvasCharts.length
            const readyCharts = apexReadyCount + canvasReadyCount
            const chartProgress = totalCharts > 0 ? readyCharts / totalCharts : 1
            onProgress(Math.min(0.95, chartProgress))

            if ((chartsReady && structureReady) || elapsed >= maxWait) {
                onProgress(1)
                resolve()
            } else {
                elapsed += checkInterval
                reliableSetTimeout(check, checkInterval)
            }
        }

        check()
    })
}

const boostFontSizes = (doc: Document, win: Window, isStructureMeasures: boolean) => {
    const boost = (selector: string, multiplier: number) => {
        doc.querySelectorAll(selector).forEach(el => {
            const computed = parseFloat(win.getComputedStyle(el).fontSize)
            if (computed > 0) {
                ;(el as HTMLElement).style.fontSize = Math.round(computed * multiplier) + 'px'
            }
        })
    }

    if (isStructureMeasures) {
        const isPMH = !!doc.querySelector('.pmh-structure-measures-page')

        boost('h1, h2, h3, .page-title, .section-title', 1.6)
        boost('th', 1.5)
        boost('td', 1.45)
        boost('p, .reporting-month, .header-sub, .header-note', 1.5)
        boost('.data-source, .footer-note, .note-text, .source-text, small', 1.4)
        boost('.structure-note, .outcome-note', 1.4)

        if (isPMH) {
            const pdfStyle = doc.createElement('style')
            pdfStyle.textContent = `
                .pmh-structure-measures-page .page-header {
                    display: flex !important;
                    flex-direction: row !important;
                    flex-wrap: nowrap !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                    gap: 24px !important;
                    margin-bottom: 16px !important;
                }
                .pmh-structure-measures-page .page-title {
                    font-size: 38px !important;
                }
                .pmh-structure-measures-page .legend-bar {
                    font-size: 18px !important;
                    flex-shrink: 0 !important;
                }
                .pmh-structure-measures-page .legend-bar span {
                    font-size: 18px !important;
                }
                .pmh-structure-measures-page .legend-dot {
                    width: 14px !important;
                    height: 14px !important;
                }
            `
            doc.head.appendChild(pdfStyle)
        }
        return
    }

    boost('h1, h2, h3, h4, h5, h6', 1.5)
    boost('.page-title, .section-title, .chart-title, .card-title, .header-title, .sub-heading', 1.5)

    boost('p, label', 1.4)
    boost('.chart-description, .info-text, .chart-subtitle, .card-subtitle', 1.4)
    boost('.header-sub, .header-note, .reporting-month', 1.4)
    boost('.legend-text, .legend-title, .legend-item', 1.4)
    boost('.measures-list, .notice-title, .notice-subtitle', 1.4)

    boost('td, th', 1.35)

    boost('.data-source, .footer-note, .note-text, .source-text, small', 1.3)
    boost('.structure-note, .outcome-note', 1.3)

    doc.querySelectorAll('.apexcharts-text tspan, .apexcharts-yaxis-label tspan, .apexcharts-xaxis-label tspan, .apexcharts-datalabel, .apexcharts-title-text, .apexcharts-legend-text').forEach(el => {
        const current = parseFloat(win.getComputedStyle(el).fontSize)
        if (current > 0) {
            const newSize = Math.round(current * 1.4) + 'px'
            ;(el as HTMLElement).style.fontSize = newSize
            el.setAttribute('font-size', newSize)
        }
    })
}

interface CaptureResult {
    canvas: HTMLCanvasElement
    splitPoints: number[]
}

const getChartSplitPoints = (doc: Document, target: HTMLElement, scale: number): number[] => {
    const targetRect = target.getBoundingClientRect()

    let chartEls = Array.from(doc.querySelectorAll('[id^="chart"]'))
    if (chartEls.length === 0) {
        chartEls = Array.from(doc.querySelectorAll('.chart-section, .chart-box, .gauge-container, .chart-container, .blue-card'))
    }
    if (chartEls.length <= 1) return []

    const rects = chartEls
        .map(el => {
            const r = (el as HTMLElement).getBoundingClientRect()
            return {
                top: (r.top - targetRect.top) * scale,
                bottom: (r.bottom - targetRect.top) * scale
            }
        })
        .filter(r => r.bottom > r.top)
        .sort((a, b) => a.top - b.top)

    const merged: { top: number; bottom: number }[] = []
    for (const r of rects) {
        const last = merged[merged.length - 1]
        if (last && r.top < last.bottom - 10) {
            last.bottom = Math.max(last.bottom, r.bottom)
        } else {
            merged.push({ ...r })
        }
    }

    if (merged.length <= 1) return []

    return merged.slice(0, -1).map((section, i) => {
        const nextSection = merged[i + 1]
        return Math.floor((section.bottom + nextSection.top) / 2)
    })
}

const captureContent = async (iframe: HTMLIFrameElement, pageTitle?: string): Promise<CaptureResult | null> => {
    const doc = iframe.contentDocument
    if (!doc) return null

    const target = doc.getElementById('dashboard') ||
        doc.querySelector('.content-wrapper') as HTMLElement ||
        doc.body

    const elementsToRestore: Array<{ element: HTMLElement; styles: Partial<CSSStyleDeclaration> }> = []

    const sidebar = doc.querySelector('.sidebar, nav.sidebar, .navigation') as HTMLElement
    if (sidebar) {
        elementsToRestore.push({
            element: sidebar,
            styles: { display: sidebar.style.display }
        })
        sidebar.style.display = 'none'
    }

    const contentArea = doc.querySelector('.content-area, .main-content') as HTMLElement
    if (contentArea) {
        elementsToRestore.push({
            element: contentArea,
            styles: {
                marginLeft: contentArea.style.marginLeft
            }
        })
        contentArea.style.marginLeft = '0'
    }

    const isStructurePage = pageTitle === 'Structure Measures'
    const targetEl = target as HTMLElement

    if (isStructurePage) {
        const structureWrapper = doc.querySelector('.pmh-structure-measures-page, .bcbs-structure-measures-page') as HTMLElement
        if (structureWrapper) {
            elementsToRestore.push({
                element: structureWrapper,
                styles: {
                    paddingLeft: structureWrapper.style.paddingLeft,
                    paddingRight: structureWrapper.style.paddingRight
                }
            })
            structureWrapper.style.paddingLeft = '4px'
            structureWrapper.style.paddingRight = '4px'
        }
    } else {
        elementsToRestore.push({
            element: targetEl,
            styles: {
                paddingLeft: targetEl.style.paddingLeft,
                paddingRight: targetEl.style.paddingRight,
                paddingTop: targetEl.style.paddingTop,
                paddingBottom: targetEl.style.paddingBottom,
                boxSizing: targetEl.style.boxSizing
            }
        })
        const isClinicEd = pageTitle === 'Clinical Team Education'
        const isPatientEd = pageTitle === 'Patient Education'
        const isOutcome = pageTitle === 'Outcome Measures'
        const padLR = isClinicEd ? '64px' : (isPatientEd || isOutcome) ? '4px' : '24px'
        targetEl.style.paddingLeft = padLR
        targetEl.style.paddingRight = padLR
        targetEl.style.paddingTop = '12px'
        targetEl.style.paddingBottom = '12px'
        targetEl.style.boxSizing = 'border-box'
    }

    const win = iframe.contentWindow
    if (win) {
        const isStructure = pageTitle === 'Structure Measures'
        boostFontSizes(doc, win, isStructure)

        const pmhPageClass = doc.querySelector('.pmh-status-at-glance-page')
            ? '.pmh-status-at-glance-page'
            : doc.querySelector('.provider-education-page')
            ? '.provider-education-page'
            : doc.querySelector('.patient-education-page')
            ? '.patient-education-page'
            : doc.querySelector('.screening-page')
            ? '.screening-page'
            : doc.querySelector('.pmh-outcome-measures-page')
            ? '.pmh-outcome-measures-page'
            : null

        if (pmhPageClass) {
            const titleSel = pmhPageClass === '.pmh-status-at-glance-page'
                ? `${pmhPageClass} .header-title`
                : `${pmhPageClass} .page-title`
            const pmhTitleStyle = doc.createElement('style')
            pmhTitleStyle.textContent = `
                ${titleSel} {
                    font-size: 38px !important;
                }
                ${pmhPageClass} .section-title {
                    font-size: 28px !important;
                }
                .patient-education-page .line-chart-outer-container {
                    align-items: center !important;
                }
            `
            doc.head.appendChild(pmhTitleStyle)
        }

        await reliableDelay(100)
    }

    const actualHeight = target.scrollHeight
    if (actualHeight > 0) {
        const minHeight = isStructurePage ? 1200 : 2200
        const newHeight = Math.max(actualHeight + 100, minHeight)
        iframe.style.height = `${newHeight}px`
        await reliableDelay(200)
    }

    const splitPoints = getChartSplitPoints(doc, target as HTMLElement, 2)

    try {
        const canvas = await html2canvas(target, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#f0f6fa',
            imageTimeout: 15000,
            removeContainer: true,
            ignoreElements: (el: any) => {
                return el.classList && (
                    el.classList.contains('hamburger') ||
                    el.classList.contains('export-menu') ||
                    el.classList.contains('export-dropdown') ||
                    el.classList.contains('menu-container') ||
                    el.classList.contains('export-menu-container') ||
                    el.classList.contains('sidebar') ||
                    el.classList.contains('navigation') ||
                    el.classList.contains('hospital-selector') ||
                    el.classList.contains('hospital-dropdown') ||
                    el.classList.contains('dropdown-header') ||
                    el.classList.contains('dropdown-list') ||
                    el.classList.contains('custom-select') ||
                    el.classList.contains('header-controls') ||
                    el.classList.contains('strat-controls-container') ||
                    el.classList.contains('interval-controls-container') ||
                    el.classList.contains('controls') ||
                    el.classList.contains('tmb-question-pill') ||
                    el.classList.contains('tmb-question-dropdown') ||
                    el.tagName === 'NAV' ||
                    el.tagName === 'HEADER' ||
                    el.tagName === 'SELECT'
                )
            }
        } as any)

        return { canvas, splitPoints }
    } finally {
        elementsToRestore.forEach(({ element, styles }) => {
            Object.keys(styles).forEach(key => {
                const value = styles[key as keyof CSSStyleDeclaration]
                if (value !== undefined) {
                    (element.style as any)[key] = value
                }
            })
        })
    }
}

const loadImage = (url: string): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'Anonymous'
        img.src = url
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            if (ctx) {
                ctx.drawImage(img, 0, 0)
                resolve({
                    dataUrl: canvas.toDataURL('image/png'),
                    width: img.width,
                    height: img.height
                })
            } else {
                resolve(null)
            }
        }
        img.onerror = () => resolve(null)
    })
}